import { useForm, Control, UseFormMethods, SubmitHandler } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import useErnie from 'shared/hooks/use-ernie';
import {
  GetMenuCustomizationDocument,
  GqlUpdateRemoteExtensionsInput,
  useUpdateRemoteExtensionsMutation,
} from 'types/graphql';
import { useStores } from 'src/hooks/use-stores';
import { useUI } from 'src/hooks/use-ui';

type HandlePublish = ReturnType<UseFormMethods<GqlUpdateRemoteExtensionsInput>['handleSubmit']>;

type UseExtensionsFormReturn = {
  control: Control<GqlUpdateRemoteExtensionsInput>;
  handlePublish: HandlePublish;
  handleReset: (this: void) => void;
  isDirty: boolean;
};

export type UseExtensionsFormProps = {
  remoteExtensions?: GqlUpdateRemoteExtensionsInput;
  applyToAll?: boolean;
};

export function useExtensionsForm(data: UseExtensionsFormProps): UseExtensionsFormReturn {
  const { id: dispensaryId } = useParams<{ id: string }>();
  const { apolloClient } = useStores();
  const { remoteExtensions, applyToAll } = data;
  const UI = useUI();

  const defaultValues = {
    builderKey: remoteExtensions?.builderKey ?? '',
    theme: {
      extensionData: remoteExtensions?.theme.extensionData ?? [],
      manifestUrl: remoteExtensions?.theme.manifestUrl ?? 'https://themes.dutchie.com/mf-manifest.json',
      modulePath: remoteExtensions?.theme.modulePath ?? '',
      scope: remoteExtensions?.theme.scope ?? '@dutchie/customer-themes',
    },
  };

  const {
    control,
    formState: { isDirty },
    handleSubmit,
    reset,
  } = useForm<GqlUpdateRemoteExtensionsInput>({ defaultValues });

  const showErnie = useErnie();

  const [updateData] = useUpdateRemoteExtensionsMutation({
    client: apolloClient,
    refetchQueries: [
      {
        query: GetMenuCustomizationDocument,
        variables: {
          dispensaryId,
        },
      },
    ],
  });

  const onSubmit: SubmitHandler<GqlUpdateRemoteExtensionsInput> = async (input) => {
    try {
      await updateData({
        variables: {
          dispensaryId,
          enterpriseId: applyToAll ? UI.dispensary.retailer?.enterpriseId : undefined,
          input,
        },
      });
      showErnie('Your extension settings have been updated', 'success');

      reset({ ...input });
    } catch (err) {
      showErnie('Something went wrong, please try again.', 'danger');
      console.error(err);
    }
  };

  const handlePublish = handleSubmit(onSubmit);

  const handleReset = (): void => reset();

  return {
    control,
    handlePublish,
    handleReset,
    isDirty,
  };
}
